import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Table,
  Button,
  Modal,
  Form,
  Spinner,
  Row,
  Col,
  Pagination,
} from "react-bootstrap";
import {
  PeopleFill,
  Search,
  GeoAltFill,
  PersonBadge,
  CheckCircleFill,
  XCircleFill,
  PersonPlusFill,
  ChevronLeft,
  ChevronRight,
  FunnelFill,
} from "react-bootstrap-icons";
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Tooltip as MuiTooltip,
} from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API_BASE_URL from "../../../config/api";

/* ── Shared style tokens (matching Users page) ── */
const primaryGradient = "linear-gradient(135deg, #1a237e, #0d47a1)";
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
};
const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

/* ── Action icon button ── */
const ActionBtn = ({ tooltip, color, onClick, disabled, children }) => (
  <MuiTooltip title={tooltip} placement="top" arrow>
    <span>
      <Button
        variant="link"
        size="sm"
        onClick={onClick}
        disabled={disabled}
        className="p-1 rounded-2"
        style={{
          color,
          lineHeight: 1,
          opacity: disabled ? 0.4 : 1,
          minWidth: "auto",
        }}
      >
        {children}
      </Button>
    </span>
  </MuiTooltip>
);

const AssignTeam = () => {
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedInstructorId, setSelectedInstructorId] = useState("");
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);

  // Pagination and filtering states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    filterLocation: "",
  });
  const [allLocations, setAllLocations] = useState([]);

  const getLocationString = (location) => {
    if (!location) return "-";
    return typeof location === "object" ? location.location : location;
  };

  // Fetch all locations from the dedicated API
  const fetchAllLocations = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(
        `${API_BASE_URL}/api/location/admin/getAllLocations`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const activeLocations = (res.data.data || [])
        .filter((loc) => loc.status === "Active")
        .map((l) => ({ _id: l._id, location: l.location }));
      setAllLocations(activeLocations);
    } catch (err) {
      console.error("Failed to fetch locations:", err);
      toast.error("Failed to load locations");
    }
  };

  const fetchInstructors = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken");

      let statusFilter = "";
      if (filters.status === "active") {
        statusFilter = "1";
      } else if (filters.status === "inactive") {
        statusFilter = "2";
      }

      const params = {
        page: currentPage,
        limit: limit,
        search: filters.search,
        status: statusFilter,
        filterLocation: filters.filterLocation,
      };

      const res = await axios.get(
        `${API_BASE_URL}/api/admin/getRegister/instructor`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        },
      );

      setInstructors(res.data.users || []);
      setTotalItems(res.data.total || 0);
      setTotalPages(Math.ceil((res.data.total || 0) / limit));
    } catch (err) {
      console.error("Failed to fetch instructors:", err);
      toast.error("Failed to fetch instructors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllLocations();
    fetchInstructors();
  }, []);

  useEffect(() => {
    fetchInstructors();
  }, [currentPage, limit]);

// ✅ Immediate fetch when page or limit changes
useEffect(() => {
  fetchInstructors();
}, [currentPage, limit]);

// ✅ Debounced fetch when filters change (2s delay)
useEffect(() => {
  const timer = setTimeout(() => {
    fetchInstructors();
  }, 2000);

  return () => clearTimeout(timer); 
}, [filters]);

// ✅ Clean handler — no debounce logic here
const handleFilterChange = (e) => {
  const { name, value } = e.target;
  setFilters((prev) => ({ ...prev, [name]: value }));
  setCurrentPage(1);
};

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleAssignClick = async (instructorId) => {
    setSelectedInstructorId(instructorId);
    try {
      setAssignLoading(true);
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(
        `${API_BASE_URL}/api/assignUsers/getUsersByLocation/${instructorId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const users = response.data.users || [];
      setAvailableUsers(users);

      // Pre-select users that are already assigned
      const preSelected = users
        .filter((user) => user.status === "Assigned")
        .map((user) => user._id);
      setSelectedUsers(preSelected);

      setShowAssignModal(true);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users");
    } finally {
      setAssignLoading(false);
    }
  };

  const handleUserSelection = (userId) => {
    setSelectedUsers((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const assignUsersToInstructor = async () => {
    if (selectedUsers.length === 0) {
      toast.error("Please select at least one user");
      return;
    }

    try {
      setAssignLoading(true);
      const token = localStorage.getItem("adminToken");
      await axios.post(
        `${API_BASE_URL}/api/assignUsers/assignToInstructor/${selectedInstructorId}`,
        { userIds: selectedUsers },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Users assigned successfully");
      setShowAssignModal(false);
      setSelectedUsers([]);
    } catch (error) {
      console.error("Error assigning users:", error);
      toast.error("Failed to assign users");
    } finally {
      setAssignLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3.5, background: "#f0f4ff", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2.5, fontSize: "0.8rem" }}>
        <Link underline="hover" color="inherit" href="/dashboard">
          Dashboard
        </Link>
        <Typography
          color="primary"
          sx={{ fontSize: "0.8rem", fontWeight: 600 }}
        >
           Assign Team
        </Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "11px",
              background: primaryGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(13,71,161,0.3)",
            }}
          >
            <PeopleFill size={20} color="#fff" />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#1a237e", lineHeight: 1.2 }}
            >
              Assign Team
            </Typography>
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              Assign users to instructors
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          p: 2,
          mb: 2.5,
          border: "1px solid #e4eaf8",
          boxShadow: "0 1px 4px rgba(13,71,161,0.05)",
        }}
      >
        <Row className="g-2 align-items-center">
          <Col md={4}>
            <Box position="relative">
              <Search
                size={13}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#aab4cc",
                  zIndex: 1,
                }}
              />
              <Form.Control
                style={{ ...fieldStyle, paddingLeft: 30 }}
                placeholder="Search by name..."
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
              />
            </Box>
          </Col>
          <Col md={3}>
            <Form.Select
              style={fieldStyle}
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Select
              style={fieldStyle}
              name="filterLocation"
              value={filters.filterLocation}
              onChange={handleFilterChange}
            >
              <option value="">All Locations</option>
              {allLocations.map((loc) => (
                <option key={loc._id} value={loc._id}>
                  {loc.location}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Select
              style={fieldStyle}
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="5">5 per page</option>
              <option value="10">10 per page</option>
              <option value="20">20 per page</option>
              <option value="50">50 per page</option>
            </Form.Select>
          </Col>
        </Row>
      </Box>

      {/* Table */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #e4eaf8",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            gap={2}
            py={8}
          >
            <Spinner
              animation="border"
              style={{
                color: "#0d47a1",
                width: 36,
                height: 36,
                borderWidth: 3,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Loading instructors...
            </Typography>
          </Box>
        ) : (
          <Table hover className="mb-0 align-middle">
            <thead style={{ background: primaryGradient }}>
              <tr>
                {["#", "Name", "Email", "Location", "Status", "Actions"].map(
                  (h) => (
                    <th
                      key={h}
                      style={{
                        color: "rgba(255,255,255,0.85)",
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        padding: "13px 16px",
                        border: "none",
                      }}
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {instructors.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-5 text-muted">
                    No instructors found.
                  </td>
                </tr>
              ) : (
                instructors.map((instructor, index) => (
                  <tr
                    key={instructor._id}
                    style={{ borderBottom: "1px solid #f0f4ff" }}
                  >
                    <td
                      style={{
                        color: "#aab4cc",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      {(currentPage - 1) * limit + index + 1}
                    </td>
                    <td style={{ fontWeight: 600, color: "#1a237e" }}>
                      {instructor.name}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {instructor.email}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {getLocationString(instructor.location)}
                    </td>
                    <td>
                      <span
                        className={`badge px-3 py-2 rounded-pill ${
                          instructor.user_status === 1
                            ? "bg-success"
                            : "bg-danger"
                        }`}
                        style={{ fontSize: "0.75rem" }}
                      >
                        {instructor.user_status === 1 ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <ActionBtn
                        tooltip="Assign Users"
                        color="#0d47a1"
                        onClick={() => handleAssignClick(instructor._id)}
                      >
                        <PersonPlusFill size={16} />
                      </ActionBtn>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}

        {/* Pagination */}
        {totalItems > 0 && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2.5}
            py={1.8}
            sx={{ borderTop: "1px solid #f0f4ff", background: "#fafbff" }}
          >
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              Showing{" "}
              <b style={{ color: "#1a237e" }}>
                {(currentPage - 1) * limit + 1}
              </b>
              –
              <b style={{ color: "#1a237e" }}>
                {Math.min(currentPage * limit, totalItems)}
              </b>{" "}
              of <b style={{ color: "#1a237e" }}>{totalItems}</b>
            </Typography>
            <Pagination size="sm" className="mb-0">
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                style={{ border: "none", background: "transparent" }}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{ border: "none", background: "transparent" }}
              />

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Pagination.Item
                    key={pageNum}
                    active={pageNum === currentPage}
                    onClick={() => handlePageChange(pageNum)}
                    style={
                      pageNum === currentPage
                        ? {
                            backgroundColor: "#1a237e",
                            borderColor: "#1a237e",
                            color: "#fff",
                          }
                        : { border: "none", background: "transparent" }
                    }
                  >
                    {pageNum}
                  </Pagination.Item>
                );
              })}

              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{ border: "none", background: "transparent" }}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                style={{ border: "none", background: "transparent" }}
              />
            </Pagination>
          </Box>
        )}
      </Box>

      {/* Assign Users Modal */}
      <Modal
        show={showAssignModal}
        onHide={() => setShowAssignModal(false)}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <PersonPlusFill size={18} /> Assign Users to Instructor
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7f9ff", padding: "1.5rem" }}>
          {assignLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              gap={2}
              py={5}
            >
              <Spinner
                animation="border"
                style={{ color: "#0d47a1", width: 36, height: 36 }}
              />
              <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
                Loading available users...
              </Typography>
            </Box>
          ) : availableUsers.length === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              gap={2}
              py={5}
            >
              <PeopleFill size={48} style={{ color: "#aab4cc" }} />
              <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
                No users available at this location
              </Typography>
            </Box>
          ) : (
            <Table hover className="mb-0 align-middle">
              <thead style={{ background: primaryGradient }}>
                <tr>
                  <th style={{ color: "#fff", padding: "10px 12px" }}>
                    Select
                  </th>
                  <th style={{ color: "#fff", padding: "10px 12px" }}>Name</th>
                  <th style={{ color: "#fff", padding: "10px 12px" }}>Email</th>
                  <th style={{ color: "#fff", padding: "10px 12px" }}>
                    Location
                  </th>
                </tr>
              </thead>
              <tbody>
                {availableUsers.map((user) => (
                  <tr
                    key={user._id}
                    style={{ borderBottom: "1px solid #f0f4ff" }}
                  >
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedUsers.includes(user._id)}
                        onChange={() => handleUserSelection(user._id)}
                        style={{ cursor: "pointer" }}
                      />
                    </td>
                    <td style={{ fontWeight: 500, color: "#1a237e" }}>
                      {user.name}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {user.email}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {getLocationString(user.location)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{ background: "#f7f9ff", borderTop: "1px solid #e4eaf8" }}
        >
          <Button
            variant="outline-secondary"
            onClick={() => setShowAssignModal(false)}
            style={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={assignUsersToInstructor}
            disabled={assignLoading || selectedUsers.length === 0}
            style={{
              background: primaryGradient,
              border: "none",
              borderRadius: "8px",
            }}
          >
            {assignLoading ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Assigning...
              </>
            ) : (
              `Assign ${selectedUsers.length} User${selectedUsers.length !== 1 ? "s" : ""}`
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Box>
  );
};

export default AssignTeam;
